import Icon from '@/components/atoms/Icon';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';

/**
 * The card every docked popover in the app is built from: the radius, border, shadow and white
 * fill, plus a sticky header carrying an optional back arrow, the heading, an optional accessory
 * and the close button; a scrolling body; and an optional footer pinned below it.
 *
 * The clip customizer's popups dock right and the asset popovers dock left (through AssetPopover),
 * but both are this card — keeping one copy is what stops the two surfaces drifting apart. It owns
 * no positioning and no open/close behaviour; callers place it and decide when it exists.
 */

export interface PopoverCardTab {
  id: string;
  label: string;
  icon?: string;
  trackingId?: string;
}

/**
 * Size, body padding and heading-row spacing. The defaults suit the customizer popups; callers
 * whose design pins the card to other dimensions (AssetPopover) override them.
 */
export interface PopoverCardClassNames {
  container?: string;
  body?: string;
  headingRow?: string;
}

type PopoverCardProps = {
  heading: string;
  onClose: () => void;
  closeTrackingId?: string;
  subHeading?: string;
  /** Rendered inline after the heading — a count badge, a pill, a status dot. */
  headingAccessory?: React.ReactNode;
  /** Renders a back arrow left of the heading. Omit it and the panel has no back affordance. */
  onBack?: () => void;
  backTrackingId?: string;
  /** Pinned below the scroll body, outside it, so it stays put as the body scrolls. */
  footer?: React.ReactNode;
  classNames?: PopoverCardClassNames;
  children: React.ReactNode;
} & (
  | { tabs?: undefined; activeTab?: never; onTabChange?: never }
  | { tabs: PopoverCardTab[]; activeTab: string; onTabChange: (id: string) => void }
);

export default function PopoverCard({
  heading,
  onClose,
  closeTrackingId = 'panel-close',
  subHeading,
  headingAccessory,
  onBack,
  backTrackingId = 'panel-back',
  footer,
  classNames,
  children,
  tabs,
  activeTab,
  onTabChange
}: PopoverCardProps) {
  const hasSubHeadingOrTabs = subHeading !== undefined || (tabs !== undefined && tabs.length > 0);

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[16px] border bg-white shadow ${
        classNames?.container ?? 'h-[calc(100vh-84px)] w-[402px]'
      }`}
    >
      <div className="sticky top-0 z-10 bg-white px-4 pt-4">
        <div
          className={`flex items-center justify-between ${
            classNames?.headingRow ?? (hasSubHeadingOrTabs ? 'mb-1' : 'mb-6')
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            {onBack && (
              <IconButton
                icon="IconArrowLeft"
                trackingId={backTrackingId}
                onClick={onBack}
                variation="none"
                title={`Back from ${heading.toLowerCase()}`}
                buttonClassName="rounded p-0.5 text-black hover:bg-slate-100 hover:text-gray-800"
              />
            )}
            <h2 className="truncate text-[18px] font-medium leading-[28px] -tracking-normal text-black">{heading}</h2>
            {headingAccessory}
          </div>
          <IconButton
            icon="IconX"
            trackingId={closeTrackingId}
            onClick={onClose}
            variation="none"
            title={`Close ${heading.toLowerCase()}`}
            buttonClassName="rounded p-0.5 text-black hover:bg-slate-100 hover:text-gray-800"
          />
        </div>
        {subHeading && <p className="mb-4 text-sm font-medium text-slate-600">{subHeading}</p>}
        {tabs && tabs.length > 0 && (
          <div className="flex border-b border-slate-200">
            {tabs.map(tab => (
              <Button
                key={tab.id}
                trackingId={tab.trackingId ?? `panel-tab-${tab.id}`}
                onClick={() => onTabChange?.(tab.id)}
                variation="none"
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-none pb-2 !text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'border-b-2 border-gray-900 text-black' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon && <Icon name={tab.icon as any} className="size-4" />}
                {tab.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <div className={`flex-1 overflow-y-auto ${classNames?.body ?? 'p-4'}`}>{children}</div>
      {footer && <div className="shrink-0">{footer}</div>}
    </div>
  );
}
